import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
// Mock data imports removed - using backend data only
import { bookingsAPI, venuesAPI, accountsAPI, leadsAPI } from '../services/api';
import { formatCurrency, formatDate, formatDateTime } from '../utils/currency';
import { formatPhoneNumber } from '../utils/validation';
import { Plus, Search, Calendar, Users, DollarSign, Phone, Mail, Edit, Pencil, Trash2, ArrowLeft, Wallet, CreditCard, Receipt, AlertCircle, Building2, RefreshCw, Bell, Loader2, Download, FileSpreadsheet, FileText, UtensilsCrossed, Palette, User, Landmark } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import TimePicker from '../components/TimePicker';
import useFinancialVisibility from '../hooks/useFinancialVisibility';
import usePermissions from '../hooks/usePermissions';
import { useOffline } from '../context/OfflineContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Cache keys and utility functions
const BOOKINGS_CACHE_KEY = 'myvenuemate_bookings_cache';
const BOOKINGS_CACHE_TIMESTAMP = 'myvenuemate_bookings_timestamp';
const VENUES_CACHE_KEY = 'myvenuemate_venues_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedBookings = () => {
  try {
    const cached = localStorage.getItem(BOOKINGS_CACHE_KEY);
    const timestamp = localStorage.getItem(BOOKINGS_CACHE_TIMESTAMP);
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }
  } catch (e) {
    console.error('Cache read error:', e);
  }
  return null;
};

const setCachedBookings = (bookings) => {
  try {
    localStorage.setItem(BOOKINGS_CACHE_KEY, JSON.stringify(bookings));
    localStorage.setItem(BOOKINGS_CACHE_TIMESTAMP, Date.now().toString());
  } catch (e) {
    console.error('Cache write error:', e);
  }
};

const getCachedVenues = () => {
  try {
    const cached = localStorage.getItem(VENUES_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    return null;
  }
};

// Event Type Options
const EVENT_TYPE_OPTIONS = [
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'RECEPTION', label: 'Reception' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'BIRTHDAY', label: 'Birthday' },
  { value: 'BABY_SHOWER', label: 'Baby Shower' },
  { value: 'OTHERS', label: 'Others' }
];

const Bookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState(() => getCachedBookings() || []);
  const [venues, setVenues] = useState(() => getCachedVenues() || []);
  const [filteredBookings, setFilteredBookings] = useState(() => getCachedBookings() || []);
  const [isLoading, setIsLoading] = useState(() => !getCachedBookings());
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all'); // New venue filter
  const [completedPaymentFilter, setCompletedPaymentFilter] = useState('all'); // Filter for completed bookings payment status
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [bookingForPayment, setBookingForPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(''); // Payment received date
  const [paymentAccountHeadId, setPaymentAccountHeadId] = useState(''); // Account head for payment
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submissions
  
  // Account Heads State
  const [accountHeads, setAccountHeads] = useState([]);
  
  // Vendor Details Dialog State
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [bookingForVendor, setBookingForVendor] = useState(null);
  const [vendorData, setVendorData] = useState({
    cateringName: '',
    cateringContact: '',
    cateringNote: '',
    decorationName: '',
    decorationContact: '',
    decorationNote: ''
  });
  
  // Lead conversion tracking
  const [convertedLeadId, setConvertedLeadId] = useState(null);
  
  const { toast } = useToast();
  const hideFinancials = useFinancialVisibility();
  const { canCreateBookings, canEditBookings, canDeleteBookings, canExportBookings, canCreateInvoices } = usePermissions();
  const { isOffline, checkOfflineAction } = useOffline();
  
  // Real-time field validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    venue: '',
    venueId: '',
    bookingType: 'confirmed', // 'confirmed' or 'blocked'
    eventType: 'WEDDING',
    customEventType: '', // For "Others" option
    eventStartDate: '',
    eventEndDate: '',
    startTime: '',
    endTime: '',
    guestCount: '',
    requirements: '',
    totalAmount: '',
    advanceAmount: '',
    advancePaymentMode: '', // Mode of payment for advance
    advancePaymentNote: '', // Note for advance payment
    advanceAccountHeadId: '', // Account head for advance payment
    payments: [], // Array of payment records
    paymentFollowupDate: '' // Date for payment follow-up reminder
  });
  
  // State for editing payments
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentBookingId, setEditPaymentBookingId] = useState(null);

  // Payment mode options
  const PAYMENT_MODE_OPTIONS = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'other', label: 'Other' }
  ];

  // Load account heads
  const loadAccountHeads = useCallback(async () => {
    try {
      const accounts = await accountsAPI.getAccountHeadsForBooking();
      console.log('[Bookings] loadAccountHeads → count:', (accounts || []).length);
      setAccountHeads(accounts || []);
      if (!accounts || accounts.length === 0) {
        toast?.({
          title: 'No accounts found',
          description: 'Your account list is empty. Create an account in the Accounts page first.',
          variant: 'default'
        });
      }
      return accounts || [];
    } catch (error) {
      console.error('[Bookings] loadAccountHeads failed:', error);
      // Don't nag staff who simply lack accounts permission - the Add Payment dialog
      // already shows a graceful "no accounts" fallback for them
      const msg = (error?.message || '').toLowerCase();
      if (!msg.includes('permission')) {
        toast?.({
          title: 'Could not load accounts',
          description: error?.message || 'Please check your internet or permissions and try again.',
          variant: 'destructive'
        });
      }
      return [];
    }
  }, [toast]);

  useEffect(() => {
    loadAccountHeads();
  }, [loadAccountHeads]);

  useEffect(() => {
    // Load with background refresh if cache exists
    const hasCache = !!getCachedBookings();
    loadBookings(hasCache);
    
    // Only load venues if not cached
    if (!getCachedVenues()) {
      loadVenues();
    }
  }, []);

  // Handle lead conversion
  useEffect(() => {
    if (location.state?.convertedLead) {
      const leadData = location.state.convertedLead;
      const leadId = location.state.leadId; // Get the lead ID for status update
      
      // Load venues first, then open dialog
      const loadAndOpen = async () => {
        await loadVenues(); // Ensure venues are loaded
        setFormData(leadData);
        setConvertedLeadId(leadId); // Store lead ID for later update
        setDialogOpen(true);
        
        // Show success message
        toast({
          title: 'Lead Converted',
          description: 'Lead data has been transferred. Please select a venue and complete the remaining booking details.',
        });

        // Clear the location state to prevent re-triggering
        navigate(location.pathname, { replace: true, state: {} });
      };
      
      loadAndOpen();
    }
  }, [location.state]);

  // Handle month filter from URL query parameter (from calendar page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const monthParam = params.get('month');
    
    if (monthParam) {
      // Parse the month parameter (format: YYYY-MM)
      const [year, month] = monthParam.split('-').map(Number);
      if (year && month) {
        // Set date range filter to custom and set the dates
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0); // Last day of the month
        
        setDateRangeFilter('custom');
        setCustomStartDate(startDate.toISOString().split('T')[0]);
        setCustomEndDate(endDate.toISOString().split('T')[0]);
      }
    }
  }, [location.search]);

  // Debounce search query for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  useEffect(() => {
    filterBookings();
  }, [bookings, debouncedSearchQuery, bookingTypeFilter, venueFilter, dateRangeFilter, customStartDate, customEndDate]);

  const loadBookings = async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) setIsLoading(true);
    
    try {
      const data = await bookingsAPI.getAll();
      // Map backend fields to frontend expected fields
      const mappedBookings = data.map(booking => ({
        id: booking.id,
        clientName: booking.customerName,
        clientEmail: booking.customerEmail,
        clientPhone: booking.customerPhone,
        venue: booking.venueName,
        venueName: booking.venueName, // Also keep venueName for reports
        venueId: booking.venueId,
        bookingType: booking.status?.toLowerCase() || 'confirmed',
        eventType: booking.eventType,
        eventStartDate: booking.eventDate,
        eventEndDate: booking.eventEndDate || booking.eventDate, // Support multi-day bookings
        startTime: booking.startTime,
        endTime: booking.endTime,
        guestCount: booking.guests,
        requirements: booking.notes || '',
        totalAmount: booking.amount,
        advanceAmount: booking.advancePaid || 0,
        advancePaymentMode: booking.advancePaymentMode || '', // Mode of payment
        payments: booking.payments || [],
        paymentFollowupDate: booking.paymentFollowupDate || '',
        capacityExceeded: booking.capacityExceeded || false, // Flag if guests exceed venue capacity
        // Vendor details
        cateringName: booking.cateringName || '',
        cateringContact: booking.cateringContact || '',
        cateringNote: booking.cateringNote || '',
        decorationName: booking.decorationName || '',
        decorationContact: booking.decorationContact || '',
        decorationNote: booking.decorationNote || '',
        // Creator and editor info
        createdByName: booking.createdByName || null,
        createdByRole: booking.createdByRole || null,
        createdAt: booking.createdAt || null,
        updatedByName: booking.updatedByName || null,
        updatedByRole: booking.updatedByRole || null,
        updatedAt: booking.updatedAt || null
      }));
      setBookings(mappedBookings);
      setCachedBookings(mappedBookings); // Cache the data
    } catch (error) {
      console.error('Failed to load bookings from backend:', error);
      if (!getCachedBookings()) {
        setBookings([]); // Empty array only if no cache
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadVenues = async () => {
    setIsLoadingVenues(true);
    
    try {
      const data = await venuesAPI.getAll();
      setVenues(data || []);
      
      if (!data || data.length === 0) {
        toast({
          title: 'No Venues Found',
          description: 'Please create a venue first in the Venues page.',
          variant: 'default'
        });
      }
      
      return data || [];
    } catch (error) {
      console.error('Failed to load venues:', error);
      setVenues([]);
      
      // Show specific error message
      const errorMessage = error.message.includes('Session expired') 
        ? 'Your session has expired. Please login again.' 
        : error.message || 'Failed to load venues. Please check your internet connection.';
      
      toast({
        title: 'Error Loading Venues',
        description: errorMessage,
        variant: 'destructive'
      });
      return [];
    } finally {
      setIsLoadingVenues(false);
    }
  };

  const getDateRangeFromFilter = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRangeFilter) {
      case 'thisWeek': {
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startDate = new Date(today);
        startDate.setDate(today.getDate() + mondayOffset);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;
      }
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        }
        break;
      default:
        return { startDate: null, endDate: null };
    }

    return { startDate, endDate };
  };

  // Memoized filter function for better performance
  const filterBookings = useCallback(() => {
    let filtered = [...bookings];

    // Search filter (using debounced value)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.clientName.toLowerCase().includes(query) ||
        b.venue.toLowerCase().includes(query) ||
        b.id.toLowerCase().includes(query)
      );
    }

    // Booking type filter (confirmed, blocked, completed, all)
    if (bookingTypeFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(b => {
        const status = (b.bookingType || b.status || '').toLowerCase();
        const eventDate = new Date(b.eventEndDate || b.eventStartDate || b.eventDate);
        eventDate.setHours(23, 59, 59, 999);
        const isCompleted = eventDate < today;
        
        if (bookingTypeFilter === 'completed') {
          // Show only completed bookings (event date has passed)
          return isCompleted && status === 'confirmed';
        } else if (bookingTypeFilter === 'confirmed') {
          // Show only upcoming confirmed bookings
          return !isCompleted && status === 'confirmed';
        } else if (bookingTypeFilter === 'blocked') {
          return status === 'blocked';
        }
        return status === bookingTypeFilter.toLowerCase();
      });
      
      // Apply payment status filter for completed bookings
      if (bookingTypeFilter === 'completed' && completedPaymentFilter !== 'all') {
        filtered = filtered.filter(b => {
          const paymentStatus = getPaymentStatus(b);
          if (completedPaymentFilter === 'cleared') {
            return paymentStatus.status === 'Paid';
          } else if (completedPaymentFilter === 'pending') {
            return paymentStatus.status !== 'Paid';
          }
          return true;
        });
      }
    }

    // Venue filter
    if (venueFilter !== 'all') {
      filtered = filtered.filter(b => b.venueId === venueFilter);
    }

    // Date range filter
    const { startDate, endDate } = getDateRangeFromFilter();
    if (startDate && endDate) {
      filtered = filtered.filter(b => {
        const bookingDate = new Date(b.eventStartDate || b.eventDate);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    // Sort by nearest booking date first (upcoming bookings first)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    filtered.sort((a, b) => {
      const dateA = new Date(a.eventStartDate || a.eventDate);
      const dateB = new Date(b.eventStartDate || b.eventDate);
      
      // Calculate days from today (negative = past, positive = future)
      const daysFromTodayA = Math.ceil((dateA - today) / (1000 * 60 * 60 * 24));
      const daysFromTodayB = Math.ceil((dateB - today) / (1000 * 60 * 60 * 24));
      
      // Upcoming events (>= 0 days) come first, sorted by nearest date
      // Past events come after, sorted by most recent first
      if (daysFromTodayA >= 0 && daysFromTodayB >= 0) {
        // Both upcoming - nearest first
        return dateA - dateB;
      } else if (daysFromTodayA < 0 && daysFromTodayB < 0) {
        // Both past - most recent first
        return dateB - dateA;
      } else {
        // Upcoming comes before past
        return daysFromTodayA >= 0 ? -1 : 1;
      }
    });

    setFilteredBookings(filtered);
  }, [bookings, debouncedSearchQuery, bookingTypeFilter, venueFilter, dateRangeFilter, customStartDate, customEndDate, completedPaymentFilter]);

  // Payment calculation helpers
  const calculateTotalPaid = (booking) => {
    const advance = Number(booking.advancePaid || booking.advanceAmount) || 0;
    const additionalPayments = (booking.payments || []).reduce((sum, p) => sum + Number(p.amount), 0);
    return advance + additionalPayments;
  };

  const calculateBalance = (booking) => {
    const total = Number(booking.amount || booking.totalAmount) || 0;
    const paid = calculateTotalPaid(booking);
    return total - paid;
  };

  const getPaymentStatus = (booking) => {
    const total = Number(booking.amount || booking.totalAmount) || 0;
    const paid = calculateTotalPaid(booking);
    
    if (paid === 0) return { status: 'Unpaid', color: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200' };
    if (paid >= total) return { status: 'Paid', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200' };
    return { status: 'Partial', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 border-yellow-200' };
  };

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || time24 === '-') return '-';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      return time24;
    }
  };

  // Download Report Functions
  // Helper to truncate venue name to 25 characters
  const truncateVenueName = (name) => {
    if (!name) return '-';
    return name.length > 25 ? name.substring(0, 25) + '...' : name;
  };

  const prepareBookingsReportData = () => {
    return filteredBookings.map((booking, index) => {
      // Get event dates
      const eventStartDate = booking.eventDate || booking.eventStartDate;
      const eventEndDate = booking.eventEndDate;
      
      // Check if it's a multi-day booking
      const isMultiDay = eventEndDate && eventEndDate !== eventStartDate;
      
      // Get venue name from multiple possible fields
      const venueName = booking.venueName || booking.venue || '-';
      
      return {
        'S.No': index + 1,
        'Client Name': booking.customerName || booking.clientName || '-',
        'Contact': booking.customerPhone || booking.clientPhone || '-',
        'Email': booking.customerEmail || booking.clientEmail || '-',
        'Event Date': formatDate(eventStartDate),
        'Event End Date': isMultiDay ? formatDate(eventEndDate) : '-',
        'Start Time': formatTimeToAMPM(booking.startTime),
        'End Time': formatTimeToAMPM(booking.endTime),
        'Event Type': booking.eventType || '-',
        'Venue': truncateVenueName(venueName),
        'Guest Count': booking.guests || booking.guestCount || '-',
        'Total Amount': Number(booking.amount || booking.totalAmount) || 0,
        'Paid Amount': calculateTotalPaid(booking),
        'Balance Amount': calculateBalance(booking),
        'Status': (booking.status === 'confirmed' || booking.bookingType === 'confirmed') ? 'Confirmed' : 
                  (booking.status === 'blocked' || booking.bookingType === 'blocked') ? 'Blocked' : 
                  (booking.status || booking.bookingType || '-'),
        'Payment Status': getPaymentStatus(booking).status
      };
    });
  };

  const downloadExcel = () => {
    try {
      const data = prepareBookingsReportData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 20 },  // Client Name
        { wch: 15 },  // Contact
        { wch: 25 },  // Email
        { wch: 12 },  // Event Date
        { wch: 12 },  // Event End Date
        { wch: 10 },  // Start Time
        { wch: 10 },  // End Time
        { wch: 15 },  // Event Type
        { wch: 20 },  // Venue
        { wch: 12 },  // Guest Count
        { wch: 15 },  // Total Amount
        { wch: 15 },  // Paid Amount
        { wch: 15 },  // Balance Amount
        { wch: 12 },  // Status
        { wch: 15 },  // Payment Status
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings Report');
      
      const fileName = `Bookings_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast({
        title: 'Download Started',
        description: `Excel report "${fileName}" is being downloaded.`
      });
    } catch (error) {
      console.error('Excel download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to generate Excel report. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const downloadPDF = () => {
    try {
      const data = prepareBookingsReportData();
      const doc = new jsPDF('landscape', 'mm', 'a4');
      
      // Helper function to format currency for PDF (without rupee symbol)
      const formatCurrencyForPDF = (amount) => {
        return 'Rs. ' + Number(amount || 0).toLocaleString('en-IN');
      };
      
      // Title
      doc.setFontSize(18);
      doc.setTextColor(20, 184, 166); // Teal color
      doc.text('Bookings Report', 14, 15);
      
      // Subtitle with date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text('Generated on: ' + new Date().toLocaleString('en-IN'), 14, 22);
      doc.text('Total Records: ' + data.length, 14, 27);

      // Table
      const tableColumn = ['S.No', 'Client Name', 'Contact', 'Event Date', 'Time', 'Event Type', 'Venue', 'Guests', 'Total', 'Paid', 'Balance', 'Status'];
      const tableRows = data.map(item => [
        item['S.No'],
        item['Client Name'],
        item['Contact'],
        item['Event Date'],
        item['Start Time'] + ' - ' + item['End Time'],
        item['Event Type'],
        item['Venue'],
        item['Guest Count'],
        formatCurrencyForPDF(item['Total Amount']),
        formatCurrencyForPDF(item['Paid Amount']),
        formatCurrencyForPDF(item['Balance Amount']),
        item['Status']
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [20, 184, 166],
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 28 },
          5: { cellWidth: 20 },
          6: { cellWidth: 25 },
          7: { cellWidth: 15 },
          8: { cellWidth: 22 },
          9: { cellWidth: 22 },
          10: { cellWidth: 22 },
          11: { cellWidth: 18 },
        }
      });

      // Summary at the bottom
      const finalY = (doc.lastAutoTable?.finalY || 150) + 10;
      const totalAmount = data.reduce((sum, item) => sum + item['Total Amount'], 0);
      const totalPaid = data.reduce((sum, item) => sum + item['Paid Amount'], 0);
      const totalBalance = data.reduce((sum, item) => sum + item['Balance Amount'], 0);
      
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text('Summary:', 14, finalY);
      doc.text('Total Amount: ' + formatCurrencyForPDF(totalAmount), 14, finalY + 6);
      doc.text('Total Paid: ' + formatCurrencyForPDF(totalPaid), 80, finalY + 6);
      doc.text('Total Balance: ' + formatCurrencyForPDF(totalBalance), 150, finalY + 6);

      const fileName = 'Bookings_Report_' + new Date().toISOString().split('T')[0] + '.pdf';
      doc.save(fileName);
      
      toast({
        title: 'Download Started',
        description: 'PDF report "' + fileName + '" is being downloaded.'
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to generate PDF report. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const checkBookingOverlap = (newBooking) => {
    // Check for overlapping bookings on same venue, date, and time
    const overlappingBookings = bookings.filter(booking => {
      // Skip if editing the same booking
      if (editingBooking && booking.id === editingBooking.id) return false;
      
      // Check if same venue
      if (booking.venueId !== newBooking.venueId) return false;
      
      // Convert times to minutes for easier comparison
      const toMinutes = (time) => {
        if (!time || !time.includes(':')) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
      };
      
      // Normalize dates to YYYY-MM-DD format for comparison
      const normalizeDate = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return date.toISOString().split('T')[0];
      };
      
      // Get existing booking dates
      const existingStartDate = normalizeDate(booking.eventStartDate || booking.eventDate);
      const existingEndDate = normalizeDate(booking.eventEndDate || booking.eventStartDate || booking.eventDate);
      
      // Get new booking dates
      const newStartDate = normalizeDate(newBooking.eventStartDate);
      const newEndDate = normalizeDate(newBooking.eventEndDate || newBooking.eventStartDate);
      
      // Check if date ranges overlap
      const datesOverlap = newStartDate <= existingEndDate && newEndDate >= existingStartDate;
      
      if (!datesOverlap) return false;
      
      // Get times in minutes
      const existingStartMin = toMinutes(booking.startTime);
      const existingEndMin = toMinutes(booking.endTime);
      const newStartMin = toMinutes(newBooking.startTime);
      const newEndMin = toMinutes(newBooking.endTime);
      
      // For multi-day bookings, we need smart time overlap detection
      // based on which day(s) of the existing booking the new booking falls on
      
      const isExistingMultiDay = existingStartDate !== existingEndDate;
      const isNewMultiDay = newStartDate !== newEndDate;
      
      // Case 1: New booking is ONLY on the LAST day of existing multi-day booking
      // Example: Existing May 26 9AM - May 28 9AM, New: May 28 10AM-12PM
      // Only check: new start >= existing end (on that last day)
      if (isExistingMultiDay && newStartDate === existingEndDate && newEndDate === existingEndDate) {
        // New booking is only on the last day of existing booking
        // Conflict only if new booking starts before existing booking ends on that day
        return newStartMin < existingEndMin;
      }
      
      // Case 1b: New booking STARTS on last day of existing and EXTENDS beyond
      // Example: Existing May 26 9AM - May 28 9AM, New: May 28 3PM - May 29 3PM
      if (isExistingMultiDay && newStartDate === existingEndDate && newEndDate > existingEndDate) {
        // Only check if new start time < existing end time on the overlap day
        return newStartMin < existingEndMin;
      }
      
      // Case 2: New booking is ONLY on the FIRST day of existing multi-day booking
      // Example: Existing May 26 9AM - May 28 9AM, New: May 26 6AM-8AM
      // Only check: new end <= existing start (on that first day)
      if (isExistingMultiDay && newStartDate === existingStartDate && newEndDate === existingStartDate) {
        // New booking is only on the first day of existing booking
        // Conflict only if new booking ends after existing booking starts on that day
        return newEndMin > existingStartMin;
      }
      
      // Case 2b: New booking ENDS on first day of existing but STARTS before
      // Example: Existing May 26 9AM - May 28 9AM, New: May 25 3PM - May 26 8AM
      if (isExistingMultiDay && newEndDate === existingStartDate && newStartDate < existingStartDate) {
        // Only check if new end time > existing start time on the overlap day
        return newEndMin > existingStartMin;
      }
      
      // Case 3: New booking spans INTO the middle of existing multi-day booking
      // Example: Existing May 26 9AM - May 28 9AM, New: May 27 anything
      // This is always a conflict (venue is occupied all day on middle days)
      if (isExistingMultiDay && newStartDate > existingStartDate && newEndDate < existingEndDate) {
        return true; // Full day conflict
      }
      
      // Case 4: Same day bookings (single day) - standard time overlap check
      if (!isExistingMultiDay && !isNewMultiDay && newStartDate === existingStartDate) {
        let adjExistingEndMin = existingEndMin;
        let adjNewEndMin = newEndMin;
        
        // Handle overnight bookings
        if (adjExistingEndMin <= existingStartMin) adjExistingEndMin += 24 * 60;
        if (adjNewEndMin <= newStartMin) adjNewEndMin += 24 * 60;
        
        // Standard overlap: A < D AND B > C
        return newStartMin < adjExistingEndMin && adjNewEndMin > existingStartMin;
      }
      
      // Case 5: Complex multi-day overlaps - check time slots
      // For any other case where dates overlap, check if times overlap
      let adjExistingEndMin = existingEndMin;
      let adjNewEndMin = newEndMin;
      
      if (adjExistingEndMin <= existingStartMin) adjExistingEndMin += 24 * 60;
      if (adjNewEndMin <= newStartMin) adjNewEndMin += 24 * 60;
      
      return newStartMin < adjExistingEndMin && adjNewEndMin > existingStartMin;
    });
    
    return overlappingBookings.length > 0 ? overlappingBookings[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (isSubmitting) return;
    

    // Validate venue is selected
    if (!formData.venueId) {
      setFieldErrors(prev => ({ ...prev, venueId: 'Please select a venue' }));
      toast({ 
        title: 'Venue Required', 
        description: 'Please select a venue for this booking. The venue field is highlighted in red.',
        variant: 'destructive'
      });
      // Scroll to venue field
      document.getElementById('venueId')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validate custom event type if "Others" is selected
    if (formData.eventType === 'OTHERS' && !formData.customEventType.trim()) {
      toast({ 
        title: 'Event Type Required', 
        description: 'Please specify the event type',
        variant: 'destructive'
      });
      return;
    }

    // Validate dates: Event Start Date must be before Event End Date
    if (formData.eventStartDate && formData.eventEndDate) {
      const startDate = new Date(formData.eventStartDate);
      const endDate = new Date(formData.eventEndDate);
      
      if (startDate > endDate) {
        toast({ 
          title: 'Invalid Date Range', 
          description: 'Event Start Date must be before or equal to Event End Date',
          variant: 'destructive'
        });
        return;
      }
    }

    // Validate times: Start Time must be before End Time (for same-day bookings)
    if (formData.startTime && formData.endTime && formData.eventStartDate === formData.eventEndDate) {
      const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
      const [endHours, endMinutes] = formData.endTime.split(':').map(Number);
      
      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;
      
      if (startTotalMinutes >= endTotalMinutes) {
        toast({ 
          title: 'Invalid Time Range', 
          description: 'For same-day bookings, Start Time must be before End Time',
          variant: 'destructive'
        });
        return;
      }
    }

    // Check for booking overlaps
    const overlap = checkBookingOverlap(formData);
    if (overlap) {
      toast({ 
        title: 'Booking Conflict', 
        description: `This venue is already booked on ${formatDate(overlap.eventStartDate || overlap.eventDate)} from ${overlap.startTime} to ${overlap.endTime}`,
        variant: 'destructive'
      });
      return;
    }

    // Validate guest count against venue capacity
    const selectedVenue = venues.find(v => v.id === formData.venueId);
    // Validate minimum booking duration (1 hour)
    const startTime = formData.startTime;
    const endTime = formData.endTime;
    
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      
      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;
      
      // Calculate duration considering multi-day bookings
      let durationMinutes;
      
      if (formData.eventStartDate === formData.eventEndDate) {
        // Same day booking - simple time difference
        durationMinutes = endTotalMinutes - startTotalMinutes;
        
        // Handle overnight bookings on same date (shouldn't happen but just in case)
        if (durationMinutes <= 0) {
          toast({ 
            title: 'Invalid Time Range', 
            description: 'For same-day bookings, End Time must be after Start Time',
            variant: 'destructive'
          });
          return;
        }
      } else {
        // Multi-day booking - calculate total duration across days
        const startDate = new Date(formData.eventStartDate);
        const endDate = new Date(formData.eventEndDate);
        const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
        
        // Duration = (days * 24 hours) + time difference
        // For same time on different days (e.g., 3PM to 3PM next day = 24 hours)
        durationMinutes = (daysDiff * 24 * 60) + (endTotalMinutes - startTotalMinutes);
      }
      
      if (durationMinutes < 60) {
        toast({ 
          title: 'Invalid Duration', 
          description: `Minimum booking duration is 1 hour. Current duration: ${durationMinutes} minutes.`,
          variant: 'destructive'
        });
        return;
      }
    }

    // Check if guest count exceeds venue capacity (warning only, not blocking)
    let capacityWarning = '';
    if (selectedVenue && Number(formData.guestCount) > selectedVenue.capacity) {
      capacityWarning = `⚠️ Note: Guest count (${formData.guestCount}) exceeds venue capacity (${selectedVenue.capacity})`;
      toast({ 
        title: '⚠️ Capacity Warning', 
        description: `Guest count (${formData.guestCount}) exceeds venue capacity (${selectedVenue.capacity}). Booking will still be created.`,
        variant: 'default'
      });
    }

    try {
      // Map frontend fields to backend expected fields
      // Validate email format if provided
      if (formData.clientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
        toast({ 
          title: 'Invalid Email', 
          description: 'Please enter a valid email address (e.g., example@domain.com)',
          variant: 'destructive'
        });
        return;
      }

      // Validate phone number (must be 10 digits only)
      const phoneDigits = formData.clientPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast({ 
          title: 'Invalid Phone Number', 
          description: 'Please enter a valid 10-digit Indian phone number',
          variant: 'destructive'
        });
        return;
      }

      // Validate guest count (must be positive)
      const guestCount = Number(formData.guestCount);
      if (guestCount <= 0 || !Number.isInteger(guestCount)) {
        toast({ 
          title: 'Invalid Guest Count', 
          description: 'Guest count must be a positive number (at least 1)',
          variant: 'destructive'
        });
        return;
      }

      // Validate total amount (must not be negative)
      const totalAmount = Number(formData.totalAmount);
      if (totalAmount < 0 || isNaN(totalAmount)) {
        toast({ 
          title: 'Invalid Total Amount', 
          description: 'Total amount cannot be negative',
          variant: 'destructive'
        });
        return;
      }

      // Validate advance amount (must not be negative)
      const advanceAmount = Number(formData.advanceAmount);
      if (advanceAmount < 0 || isNaN(advanceAmount)) {
        toast({ 
          title: 'Invalid Advance Amount', 
          description: 'Advance amount cannot be negative',
          variant: 'destructive'
        });
        return;
      }

      // Validate advance amount doesn't exceed total amount
      if (advanceAmount > totalAmount) {
        toast({ 
          title: 'Invalid Advance Amount', 
          description: 'Advance amount cannot exceed total amount',
          variant: 'destructive'
        });
        return;
      }

      // Start submitting - disable button
      setIsSubmitting(true);

      // Determine final event type (use custom if "Others" selected)
      const finalEventType = formData.eventType === 'OTHERS' 
        ? formData.customEventType 
        : EVENT_TYPE_OPTIONS.find(o => o.value === formData.eventType)?.label || formData.eventType;

      // Combine requirements with capacity warning if exists
      const notesWithWarning = capacityWarning 
        ? (formData.requirements ? `${formData.requirements}\n\n${capacityWarning}` : capacityWarning)
        : (formData.requirements || '');
      
      const bookingData = {
        venueId: formData.venueId,
        venueName: formData.venue,
        customerName: formData.clientName,
        customerPhone: formData.clientPhone,
        eventType: finalEventType,
        eventDate: formData.eventStartDate,
        eventEndDate: formData.eventEndDate || formData.eventStartDate, // Support multi-day bookings
        startTime: formData.startTime,
        endTime: formData.endTime,
        guests: guestCount,
        amount: totalAmount,
        advancePaid: advanceAmount,
        advancePaymentMode: formData.advancePaymentMode || null, // Mode of payment
        advancePaymentNote: formData.advancePaymentNote || null, // Note for advance payment
        advanceAccountHeadId: formData.advanceAccountHeadId || null, // Account head for advance
        status: formData.bookingType === 'blocked' ? 'blocked' : 'confirmed',
        notes: notesWithWarning,
        capacityExceeded: !!capacityWarning, // Flag for UI display
        paymentFollowupDate: formData.paymentFollowupDate || null
      };

      // Add email only if provided
      if (formData.clientEmail) {
        bookingData.customerEmail = formData.clientEmail;
      }

      if (editingBooking) {
        const result = await bookingsAPI.update(editingBooking.id, bookingData);
        toast({ title: 'Success', description: 'Booking updated successfully' });
      } else {
        await bookingsAPI.create(bookingData);
        toast({ title: 'Success', description: 'Booking created successfully' });
        
        // If this booking was created from a lead conversion, update the lead status to "won"
        if (convertedLeadId) {
          try {
            await leadsAPI.update(convertedLeadId, { status: 'won' });
            toast({ 
              title: 'Lead Updated', 
              description: 'Lead status has been changed to "Won"' 
            });
          } catch (leadError) {
            console.error('Failed to update lead status:', leadError);
            // Don't fail the whole operation if lead update fails
          }
          setConvertedLeadId(null); // Clear the converted lead ID
        }
      }

      // CRITICAL: Clear cache before reloading to ensure fresh data
      localStorage.removeItem(BOOKINGS_CACHE_KEY);
      localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
      
      // Close dialog first for better UX
      resetForm();
      setDialogOpen(false);
      
      // Force reload from backend (not from cache)
      await loadBookings(false); // false = not background refresh, show loading
    } catch (error) {
      console.error('Failed to save booking:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to save booking',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  const handleEdit = (booking) => {
    // Block edit when offline
    if (!checkOfflineAction('edit')) return;
    
    setEditingBooking(booking);
    
    // Map old field names to new field names for backward compatibility
    // FIX: Handle bookings that were created without a venue
    let venueId = booking.venueId || '';
    let venueName = booking.venue || booking.venueName || '';
    
    // If we have a venue name but no venueId, try to find the venue by name
    if (!venueId && venueName && venues.length > 0) {
      const matchedVenue = venues.find(v => 
        v.name.toLowerCase() === venueName.toLowerCase() ||
        v.name.toLowerCase().includes(venueName.toLowerCase()) ||
        venueName.toLowerCase().includes(v.name.toLowerCase())
      );
      if (matchedVenue) {
        venueId = matchedVenue.id;
        venueName = matchedVenue.name;
      }
    }
    
    // If still no venueId and we have venues, show a warning
    if (!venueId && venues.length > 0) {
      console.warn('⚠️ Booking has no venue assigned. User must select one.');
      toast({
        title: 'Venue Required',
        description: 'This booking has no venue assigned. Please select a venue before saving.',
        variant: 'destructive'
      });
    }
    
    const mappedData = {
      clientName: booking.clientName || booking.customerName || '',
      clientEmail: booking.clientEmail || booking.customerEmail || '',
      clientPhone: booking.clientPhone || booking.customerPhone || '',
      venue: venueName,
      venueId: venueId,
      bookingType: booking.bookingType || booking.status || 'confirmed',
      // Check if eventType matches predefined options, otherwise set as "OTHERS"
      eventType: EVENT_TYPE_OPTIONS.some(o => o.label === booking.eventType || o.value === booking.eventType) 
        ? (EVENT_TYPE_OPTIONS.find(o => o.label === booking.eventType)?.value || booking.eventType)
        : 'OTHERS',
      customEventType: EVENT_TYPE_OPTIONS.some(o => o.label === booking.eventType || o.value === booking.eventType) 
        ? '' 
        : (booking.eventType || ''),
      eventStartDate: booking.eventStartDate || booking.eventDate || '',
      eventEndDate: booking.eventEndDate || booking.eventDate || '',
      startTime: booking.startTime || '',
      endTime: booking.endTime || '',
      guestCount: booking.guestCount || booking.guests || '',
      requirements: booking.requirements || booking.notes || '',
      totalAmount: booking.totalAmount || booking.amount || '',
      advanceAmount: booking.advanceAmount || booking.advancePaid || '',
      advancePaymentMode: booking.advancePaymentMode || '',
      advancePaymentNote: booking.advancePaymentNote || '',
      payments: booking.payments || [],
      paymentFollowupDate: booking.paymentFollowupDate || ''
    };
    
    setFormData(mappedData);
    setDialogOpen(true);
    // Refresh accounts when opening edit (handles cases where on-mount fetch failed)
    loadAccountHeads();
  };

  const handleDeleteClick = (booking) => {
    // Block delete when offline
    if (!checkOfflineAction('delete')) return;
    
    setBookingToDelete(booking);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (bookingToDelete) {
      try {
        await bookingsAPI.delete(bookingToDelete.id);
        toast({ title: 'Success', description: 'Booking deleted successfully' });
        // Clear cache before reloading
        localStorage.removeItem(BOOKINGS_CACHE_KEY);
        localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
        await loadBookings(false); // Reload from backend
        setDeleteConfirmOpen(false);
        setBookingToDelete(null);
      } catch (error) {
        console.error('Failed to delete booking:', error);
        toast({ 
          title: 'Error', 
          description: error.message || 'Failed to delete booking',
          variant: 'destructive'
        });
      }
    }
  };

  const handleAddPaymentClick = (booking) => {
    setBookingForPayment(booking);
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentNotes('');
    setPaymentDate(new Date().toISOString().split('T')[0]); // Default to today
    setPaymentAccountHeadId('');
    setPaymentDialogOpen(true);
    // Always refresh account heads so the dropdown is populated even if the
    // initial on-mount fetch failed or the user just created a new account.
    loadAccountHeads();
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast({ 
        title: 'Invalid Amount', 
        description: 'Please enter a valid payment amount',
        variant: 'destructive'
      });
      return;
    }

    const balance = calculateBalance(bookingForPayment);
    if (Number(paymentAmount) > balance) {
      toast({ 
        title: 'Amount Exceeds Balance', 
        description: `Payment amount cannot exceed remaining balance of ${formatCurrency(balance)}`,
        variant: 'destructive'
      });
      return;
    }

    try {
      const newPayment = {
        id: Date.now().toString(),
        amount: Number(paymentAmount),
        method: paymentMethod,
        accountHeadId: paymentAccountHeadId || null, // Account head for income tracking
        notes: paymentNotes,
        date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString() // Use selected date or current date
      };

      // Call backend API to record payment
      await bookingsAPI.addPayment(bookingForPayment.id, newPayment);
      
      toast({ 
        title: 'Payment Recorded', 
        description: `Payment of ${formatCurrency(Number(paymentAmount))} added successfully` 
      });

      // Clear cache before reloading
      localStorage.removeItem(BOOKINGS_CACHE_KEY);
      localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
      
      // Reload bookings from backend
      await loadBookings(false);
      
      // Close dialog
      setPaymentDialogOpen(false);
      setBookingForPayment(null);
      setEditingPayment(null);
      setPaymentAccountHeadId(''); // Reset account head
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to record payment',
        variant: 'destructive'
      });
    }
  };

  // Handle edit payment - populate the dialog with existing payment data
  const handleEditPayment = (booking, payment) => {
    setBookingForPayment(booking);
    setEditingPayment(payment);
    setEditPaymentBookingId(booking.id);
    setPaymentAmount(payment.amount.toString());
    setPaymentMethod(payment.method || 'cash');
    setPaymentAccountHeadId(payment.accountHeadId || ''); // Load account head
    setPaymentNotes(payment.notes || '');
    // Load existing payment date or default to today
    setPaymentDate(payment.date ? new Date(payment.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setPaymentDialogOpen(true);
    // Always refresh account heads so the dropdown is populated on edit too
    loadAccountHeads();
  };

  // Handle update payment
  const handleUpdatePayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast({ 
        title: 'Invalid Amount', 
        description: 'Please enter a valid payment amount',
        variant: 'destructive'
      });
      return;
    }

    try {
      const updatedPayment = {
        ...editingPayment,
        amount: Number(paymentAmount),
        method: paymentMethod,
        accountHeadId: paymentAccountHeadId || null, // Include account head in update
        notes: paymentNotes,
        date: paymentDate ? new Date(paymentDate).toISOString() : editingPayment.date, // Use selected date or keep existing
        updatedAt: new Date().toISOString()
      };

      // Call backend API to update payment
      await bookingsAPI.updatePayment(editPaymentBookingId, editingPayment.id, updatedPayment);
      
      toast({ 
        title: 'Payment Updated', 
        description: `Payment updated successfully` 
      });

      // Clear cache and reload
      localStorage.removeItem(BOOKINGS_CACHE_KEY);
      localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
      await loadBookings(false);
      
      // Close dialog and reset
      setPaymentDialogOpen(false);
      setBookingForPayment(null);
      setEditingPayment(null);
      setEditPaymentBookingId(null);
    } catch (error) {
      console.error('Failed to update payment:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update payment',
        variant: 'destructive'
      });
    }
  };

  // Handle delete payment
  const handleDeletePayment = async (booking, paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      await bookingsAPI.deletePayment(booking.id, paymentId);
      
      toast({ 
        title: 'Payment Deleted', 
        description: 'Payment has been removed successfully' 
      });

      // Clear cache and reload
      localStorage.removeItem(BOOKINGS_CACHE_KEY);
      localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
      await loadBookings(false);
    } catch (error) {
      console.error('Failed to delete payment:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to delete payment',
        variant: 'destructive'
      });
    }
  };

  // Handle Vendor Details
  const handleVendorClick = (booking) => {
    setBookingForVendor(booking);
    setVendorData({
      cateringName: booking.cateringName || '',
      cateringContact: booking.cateringContact || '',
      cateringNote: booking.cateringNote || '',
      decorationName: booking.decorationName || '',
      decorationContact: booking.decorationContact || '',
      decorationNote: booking.decorationNote || ''
    });
    setVendorDialogOpen(true);
  };

  const handleVendorSubmit = async () => {
    if (!bookingForVendor) return;

    try {
      await bookingsAPI.update(bookingForVendor.id, vendorData);
      
      toast({ 
        title: 'Vendor Details Saved', 
        description: 'Catering and decoration details have been updated' 
      });

      // Clear cache and reload
      localStorage.removeItem(BOOKINGS_CACHE_KEY);
      localStorage.removeItem(BOOKINGS_CACHE_TIMESTAMP);
      await loadBookings(false);
      setVendorDialogOpen(false);
      setBookingForVendor(null);
    } catch (error) {
      console.error('Failed to save vendor details:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to save vendor details',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      venue: '',
      venueId: '',
      bookingType: 'confirmed',
      eventType: 'WEDDING',
      customEventType: '',
      eventStartDate: '',
      eventEndDate: '',
      startTime: '',
      endTime: '',
      guestCount: '',
      requirements: '',
      totalAmount: '',
      advanceAmount: '',
      advancePaymentMode: '',
      advancePaymentNote: '',
      payments: [],
      paymentFollowupDate: ''
    });
    setEditingBooking(null);
    setEditingPayment(null);
    setEditPaymentBookingId(null);
    setFieldErrors({}); // Clear validation errors
    setConvertedLeadId(null); // Clear converted lead ID
  };

  // Real-time field validation
  const validateField = (fieldName, value, allFormData = formData) => {
    let error = '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (fieldName) {
      case 'clientName':
        if (!value || value.trim() === '') {
          error = 'Client name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
        
      case 'clientPhone':
        if (!value || value.trim() === '') {
          error = 'Phone number is required';
        } else {
          const digits = value.replace(/\D/g, '');
          if (digits.length !== 10) {
            error = 'Phone must be exactly 10 digits';
          }
        }
        break;
        
      case 'clientEmail':
        if (value && value.trim() !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = 'Invalid email format (e.g., abc@domain.com)';
          }
        }
        break;
        
      case 'venueId':
        if (!value) {
          error = 'Please select a venue';
        }
        break;
        
      case 'eventStartDate':
        if (!value) {
          error = 'Start date is required';
        } else {
          const startDate = new Date(value);
          if (startDate < today) {
            error = 'Start date cannot be in the past';
          }
        }
        break;
        
      case 'eventEndDate':
        if (!value) {
          error = 'End date is required';
        } else if (allFormData.eventStartDate) {
          const startDate = new Date(allFormData.eventStartDate);
          const endDate = new Date(value);
          if (endDate < startDate) {
            error = 'End date must be after start date';
          }
        }
        break;
        
      case 'startTime':
        if (!value) {
          error = 'Start time is required';
        }
        break;
        
      case 'endTime':
        if (!value) {
          error = 'End time is required';
        }
        break;
        
      case 'guestCount':
        if (!value || value === '') {
          error = 'Guest count is required';
        } else {
          const count = Number(value);
          if (isNaN(count) || count <= 0 || !Number.isInteger(count)) {
            error = 'Must be a positive whole number';
          }
        }
        break;
        
      case 'totalAmount':
        if (value && value !== '') {
          const amount = Number(value);
          if (isNaN(amount) || amount < 0) {
            error = 'Amount cannot be negative';
          }
        }
        break;
        
      case 'advanceAmount':
        if (value && value !== '') {
          const advance = Number(value);
          const total = Number(allFormData.totalAmount) || 0;
          if (isNaN(advance) || advance < 0) {
            error = 'Advance cannot be negative';
          } else if (advance > total && total > 0) {
            error = 'Advance cannot exceed total amount';
          }
        }
        break;
        
      case 'customEventType':
        if (allFormData.eventType === 'OTHERS' && (!value || value.trim() === '')) {
          error = 'Please specify the event type';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Handle field change with real-time validation
  const handleFieldChange = (fieldName, value) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    
    // Validate the field
    const error = validateField(fieldName, value, newFormData);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    // Also validate related fields
    if (fieldName === 'eventStartDate' && newFormData.eventEndDate) {
      const endError = validateField('eventEndDate', newFormData.eventEndDate, newFormData);
      setFieldErrors(prev => ({ ...prev, eventEndDate: endError }));
    }
    if (fieldName === 'totalAmount' && newFormData.advanceAmount) {
      const advError = validateField('advanceAmount', newFormData.advanceAmount, newFormData);
      setFieldErrors(prev => ({ ...prev, advanceAmount: advError }));
    }
    if (fieldName === 'eventType') {
      const customError = validateField('customEventType', newFormData.customEventType, newFormData);
      setFieldErrors(prev => ({ ...prev, customEventType: customError }));
    }
  };

  // Validate field on blur
  const handleFieldBlur = (fieldName) => {
    const error = validateField(fieldName, formData[fieldName]);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const handleVenueChange = (venueId) => {
    const selectedVenue = venues.find(v => v.id === venueId);
    
    // Use functional update to avoid stale closure
    setFormData(prevFormData => {
      const newFormData = {
        ...prevFormData,
        venueId: venueId,
        venue: selectedVenue ? selectedVenue.name : ''
      };
      return newFormData;
    });
    
    // Clear venue error if venue is selected
    if (venueId && selectedVenue) {
      setFieldErrors(prev => ({ ...prev, venueId: '' }));
    }
  };

  const getBookingTypeColor = (type) => {
    const colors = {
      confirmed: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700',
      blocked: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700'
    };
    return colors[type] || colors.confirmed;
  };

  const getBookingTypeLabel = (type) => {
    return type === 'blocked' ? 'Blocked Date' : 'Confirmed Booking';
  };

  // Format role for display (capitalize first letter)
  const formatRole = (role) => {
    if (!role) return '';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="rounded-full hover:bg-gray-100 dark:bg-secondary h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-foreground truncate">
              Bookings
              {isLoading && <RefreshCw className="w-4 h-4 ml-2 inline animate-spin text-teal-500" />}
            </h1>
            <p className="text-xs sm:text-base text-gray-600 dark:text-muted-foreground mt-0.5 sm:mt-1">Manage venue bookings</p>
          </div>
        </div>
        {canCreateBookings() && (
          <Button 
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-md w-full sm:w-auto"
            onClick={() => {
              if (!checkOfflineAction('create')) return;
              setDialogOpen(true);
              if (venues.length === 0) {
                loadVenues();
              }
              // Always refresh accounts so the "Advance Received In" dropdown is populated
              loadAccountHeads();
            }}
            disabled={isOffline}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Booking
          </Button>
        )}
        <Dialog open={dialogOpen} onOpenChange={(open) => { 
          if (!open) {
            resetForm();
          }
          setDialogOpen(open);
        }}>
          <DialogContent className="w-full sm:w-[95vw] max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-2 sm:pb-4">
              <DialogTitle className="text-lg sm:text-xl">{editingBooking ? 'Edit Booking' : 'Create New Booking'}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {editingBooking ? 'Update booking information' : 'Add a new booking to the system'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 mt-2 sm:mt-4 pb-32 sm:pb-0 w-full">
              {/* Client Information */}
              <div className="space-y-2 sm:space-y-4 w-full">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-foreground flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  Client Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientName" className={fieldErrors.clientName ? 'text-red-600' : ''}>Client Name *</Label>
                    <Input 
                      id="clientName" 
                      value={formData.clientName} 
                      onChange={(e) => handleFieldChange('clientName', e.target.value)}
                      onBlur={() => handleFieldBlur('clientName')}
                      className={fieldErrors.clientName ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                      required 
                    />
                    {fieldErrors.clientName && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.clientName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientEmail" className={fieldErrors.clientEmail ? 'text-red-600' : ''}>Email</Label>
                    <Input 
                      id="clientEmail" 
                      type="email" 
                      value={formData.clientEmail} 
                      onChange={(e) => handleFieldChange('clientEmail', e.target.value)}
                      onBlur={() => handleFieldBlur('clientEmail')}
                      placeholder="Optional"
                      className={fieldErrors.clientEmail ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                    />
                    {fieldErrors.clientEmail && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.clientEmail}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientPhone" className={fieldErrors.clientPhone ? 'text-red-600' : ''}>Phone * (10 digits)</Label>
                    <Input 
                      id="clientPhone" 
                      type="tel"
                      placeholder="9876543210"
                      value={formData.clientPhone} 
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleFieldChange('clientPhone', digitsOnly);
                      }}
                      onBlur={() => handleFieldBlur('clientPhone')}
                      maxLength="10"
                      required
                      className={fieldErrors.clientPhone ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                    />
                    {fieldErrors.clientPhone && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.clientPhone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="space-y-2 sm:space-y-4 w-full">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  Event Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="venue">Venue *</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={loadVenues}
                        disabled={isLoadingVenues}
                        className="h-6 px-2 text-xs"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${isLoadingVenues ? 'animate-spin' : ''}`} />
                        {isLoadingVenues ? 'Loading...' : 'Refresh'}
                      </Button>
                    </div>
                    {isLoadingVenues ? (
                      <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-border rounded-lg">
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin text-gray-400 dark:text-muted-foreground" />
                        <span className="text-sm text-gray-600 dark:text-muted-foreground">Loading venues...</span>
                      </div>
                    ) : venues.length === 0 ? (
                      <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-900/20">
                        <AlertCircle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-800">
                          <strong>No venues found!</strong> Please create a venue first.
                          <Button 
                            type="button"
                            variant="link" 
                            className="p-0 h-auto font-semibold text-orange-700 ml-1"
                            onClick={() => {
                              setDialogOpen(false);
                              navigate('/venues');
                            }}
                          >
                            Go to Venues →
                          </Button>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <select
                          id="venue"
                          value={formData.venueId || ''}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            handleVenueChange(selectedValue);
                            // Don't call handleFieldChange - handleVenueChange handles everything
                          }}
                          onBlur={() => handleFieldBlur('venueId')}
                          required
                          className={`flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border bg-background text-foreground px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${fieldErrors.venueId ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : 'border-input focus:ring-ring'}`}
                        >
                          <option value="" className="bg-background text-foreground dark:bg-gray-800 dark:text-gray-100">Select a venue</option>
                          {venues.map((venue) => (
                            <option key={venue.id} value={venue.id} className="bg-background text-foreground dark:bg-gray-800 dark:text-gray-100">
                              {venue.name} (Capacity: {venue.capacity})
                            </option>
                          ))}
                        </select>
                        {fieldErrors.venueId && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {fieldErrors.venueId}
                          </p>
                        )}
                        {formData.venueId && venues.find(v => v.id === formData.venueId) && (
                          <p className="text-xs text-gray-500 dark:text-muted-foreground flex items-center">
                            <Building2 className="w-3 h-3 mr-1" />
                            {venues.find(v => v.id === formData.venueId).location} • Max: {venues.find(v => v.id === formData.venueId).capacity} guests
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eventType">Event Type *</Label>
                    <Select 
                      value={formData.eventType} 
                      onValueChange={(value) => setFormData({...formData, eventType: value, customEventType: value === 'OTHERS' ? formData.customEventType : ''})}
                    >
                      <SelectTrigger className="h-11 sm:h-10">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Custom Event Type - shows when "Others" is selected */}
                {formData.eventType === 'OTHERS' && (
                  <div className="space-y-2">
                    <Label htmlFor="customEventType">Specify Event Type *</Label>
                    <Input 
                      id="customEventType" 
                      value={formData.customEventType} 
                      onChange={(e) => setFormData({...formData, customEventType: e.target.value})} 
                      placeholder="Enter event type"
                      required 
                      className="h-11 sm:h-10"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="eventStartDate" className={`text-xs sm:text-sm md:text-base ${fieldErrors.eventStartDate ? 'text-red-600' : ''}`}>Start Date *</Label>
                    <Input 
                      type="date"
                      value={formData.eventStartDate} 
                      onChange={(e) => handleFieldChange('eventStartDate', e.target.value)}
                      className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                      required
                    />
                    {fieldErrors.eventStartDate && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.eventStartDate}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="eventEndDate" className={`text-xs sm:text-sm md:text-base ${fieldErrors.eventEndDate ? 'text-red-600' : ''}`}>End Date *</Label>
                    <Input 
                      type="date"
                      value={formData.eventEndDate} 
                      onChange={(e) => handleFieldChange('eventEndDate', e.target.value)}
                      min={formData.eventStartDate}
                      className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                      required
                    />
                    {fieldErrors.eventEndDate && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.eventEndDate}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="startTime" className={`text-xs sm:text-base ${fieldErrors.startTime ? 'text-red-600' : ''}`}>Start Time *</Label>
                    <TimePicker
                      id="startTime"
                      value={formData.startTime}
                      onChange={(time) => handleFieldChange('startTime', time)}
                      onComplete={() => {}}
                      required
                      className={fieldErrors.startTime ? 'border-red-500' : ''}
                    />
                    {fieldErrors.startTime && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.startTime}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="endTime" className={`text-xs sm:text-base ${fieldErrors.endTime ? 'text-red-600' : ''}`}>End Time *</Label>
                    <TimePicker
                      id="endTime-input"
                      value={formData.endTime}
                      onChange={(time) => handleFieldChange('endTime', time)}
                      onComplete={() => {}}
                      required
                      className={fieldErrors.endTime ? 'border-red-500' : ''}
                    />
                    {fieldErrors.endTime && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.endTime}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="guestCount" className={`text-xs sm:text-base ${fieldErrors.guestCount ? 'text-red-600' : ''}`}>Guest Count *</Label>
                    <Input 
                      id="guestCount" 
                      type="number" 
                      min="1" 
                      value={formData.guestCount} 
                      onChange={(e) => handleFieldChange('guestCount', e.target.value)}
                      onBlur={() => handleFieldBlur('guestCount')}
                      required 
                      className={`h-10 ${fieldErrors.guestCount ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
                    />
                    {fieldErrors.guestCount && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.guestCount}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="requirements" className="text-xs sm:text-base">Requirements</Label>
                    <Input 
                      id="requirements" 
                      value={formData.requirements} 
                      onChange={(e) => setFormData({...formData, requirements: e.target.value})} 
                      placeholder="Special requests"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="space-y-2 sm:space-y-4 w-full">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  Payment Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalAmount" className={fieldErrors.totalAmount ? 'text-red-600' : ''}>Total Amount *</Label>
                    <Input 
                      id="totalAmount" 
                      type="number" 
                      min="0" 
                      step="0.01"
                      value={formData.totalAmount} 
                      onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
                      onBlur={() => handleFieldBlur('totalAmount')}
                      required 
                      className={fieldErrors.totalAmount ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                    />
                    {fieldErrors.totalAmount && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.totalAmount}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="advanceAmount" className={fieldErrors.advanceAmount ? 'text-red-600' : ''}>Advance Paid *</Label>
                    <Input 
                      id="advanceAmount" 
                      type="number" 
                      min="0" 
                      step="0.01"
                      value={formData.advanceAmount} 
                      onChange={(e) => handleFieldChange('advanceAmount', e.target.value)}
                      onBlur={() => handleFieldBlur('advanceAmount')}
                      required 
                      className={fieldErrors.advanceAmount ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                    />
                    {fieldErrors.advanceAmount && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.advanceAmount}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="advancePaymentMode">Payment Mode</Label>
                    <Select 
                      value={formData.advancePaymentMode} 
                      onValueChange={(value) => setFormData({...formData, advancePaymentMode: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_MODE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="advancePaymentNote">Advance Payment Note</Label>
                    <Input 
                      id="advancePaymentNote"
                      placeholder="Transaction ID, reference, or any notes for advance payment"
                      value={formData.advancePaymentNote} 
                      onChange={(e) => setFormData({...formData, advancePaymentNote: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="advanceAccountHeadId" className="flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-blue-500" />
                      Advance Received In
                    </Label>
                    {accountHeads.length === 0 ? (
                      <div className="border border-orange-200 dark:border-orange-900 rounded-md p-3 bg-orange-50 dark:bg-orange-950/30">
                        <p className="text-xs text-orange-800 dark:text-orange-200 mb-2">
                          No accounts available.
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => loadAccountHeads()}
                            className="h-7 text-xs"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Reload accounts
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => { setDialogOpen(false); navigate('/accounts'); }}
                            className="h-7 text-xs"
                          >
                            Go to Accounts page
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Select 
                        value={formData.advanceAccountHeadId} 
                        onValueChange={(value) => setFormData({...formData, advanceAccountHeadId: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {accountHeads.map(account => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">
                      Which account did you receive the advance in?
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paymentFollowupDate" className="flex items-center gap-2 text-xs sm:text-sm md:text-base">
                      <Bell className="w-4 h-4 text-orange-500" />
                      Payment Follow-up Reminder
                    </Label>
                    <Input 
                      type="date"
                      value={formData.paymentFollowupDate} 
                      onChange={(e) => setFormData({...formData, paymentFollowupDate: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                    />
                    <p className="text-xs text-gray-500 dark:text-muted-foreground">
                      You&apos;ll receive an in-app notification on this date
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bookingType">Booking Type *</Label>
                    <Select value={formData.bookingType} onValueChange={(value) => setFormData({...formData, bookingType: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Confirmed Booking</SelectItem>
                        <SelectItem value="blocked">Block Date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Submit Buttons - Fixed at bottom on mobile */}
              <div className="fixed sm:relative bottom-0 left-0 right-0 sm:bottom-auto sm:left-auto sm:right-auto flex flex-row justify-end gap-2 sm:gap-3 p-3 sm:p-0 sm:pt-6 bg-white dark:bg-card border-t sm:border-t-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none z-[100] sm:z-auto safe-area-pb">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setDialogOpen(false); resetForm(); }} 
                  className="h-12 sm:h-10 flex-1 sm:flex-none max-w-[120px] sm:max-w-none text-sm touch-manipulation"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Find the form and submit it
                    const form = e.target.closest('form');
                    if (form) {
                      form.requestSubmit();
                    }
                  }}
                  className="h-12 sm:h-10 flex-1 sm:flex-none max-w-[180px] sm:max-w-none bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-sm touch-manipulation"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingBooking ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>{editingBooking ? 'Update' : 'Create'} Booking</>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats - 2 columns on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-500 dark:text-muted-foreground truncate">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-600">{bookings.length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-500 dark:text-muted-foreground truncate">Confirmed</p>
                <p className="text-lg sm:text-2xl font-bold text-green-600">
                  {bookings.filter(b => b.bookingType === 'confirmed' || b.status === 'confirmed').length}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-500 dark:text-muted-foreground truncate">Blocked</p>
                <p className="text-lg sm:text-2xl font-bold text-red-600">
                  {bookings.filter(b => b.bookingType === 'blocked').length}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-sm cursor-pointer active:bg-gray-50 dark:bg-secondary"
          onClick={() => {
            setBookingTypeFilter('completed');
            setCompletedPaymentFilter('all');
          }}
        >
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-500 dark:text-muted-foreground truncate">Completed</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-600">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return bookings.filter(b => {
                      const status = (b.bookingType || b.status || '').toLowerCase();
                      const eventDate = new Date(b.eventEndDate || b.eventStartDate || b.eventDate);
                      eventDate.setHours(23, 59, 59, 999);
                      return status === 'confirmed' && eventDate < today;
                    }).length;
                  })()}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-3 sm:p-6">
          {/* Search - Full width on mobile */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by name, venue..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-sm h-10"
            />
          </div>
          
          {/* Filters - Grid on mobile for proper sizing */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {/* Booking Type Filter */}
            <Select value={bookingTypeFilter} onValueChange={(value) => {
              setBookingTypeFilter(value);
              if (value !== 'completed') {
                setCompletedPaymentFilter('all');
              }
            }}>
              <SelectTrigger className="text-xs sm:text-sm h-9">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="confirmed">Upcoming</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            {/* Venue Filter */}
            <Select value={venueFilter} onValueChange={setVenueFilter}>
              <SelectTrigger className="text-xs sm:text-sm h-9">
                <SelectValue placeholder="Venue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {venues.map(venue => (
                  <SelectItem key={venue.id} value={venue.id}>
                    {venue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Range Filter */}
            <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
              <SelectTrigger className="text-xs sm:text-sm h-9">
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="thisWeek">This Week</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="thisYear">This Year</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>

            {/* Payment Status Filter (only for completed bookings) */}
            {bookingTypeFilter === 'completed' ? (
              <Select value={completedPaymentFilter} onValueChange={setCompletedPaymentFilter}>
                <SelectTrigger className="text-xs sm:text-sm h-9">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="cleared">Cleared</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="hidden sm:block" /> 
            )}
          </div>

          {/* Custom Date Range */}
          {dateRangeFilter === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="customStartDate" className="text-xs sm:text-sm md:text-base">Start Date</Label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                />
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="customEndDate" className="text-xs sm:text-sm md:text-base">End Date</Label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                />
              </div>
            </div>
          )}

          {/* Download Report Buttons */}
          {canExportBookings() && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-3 pt-3 border-t">
              <div className="flex items-center justify-between sm:justify-start gap-2 text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-medium">Download:</span>
                </div>
                <span className="text-[10px] sm:text-xs text-gray-400 dark:text-muted-foreground sm:hidden">
                  {filteredBookings.length} records
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadExcel}
                  className="h-9 hover:bg-green-50 dark:bg-green-900/20 hover:border-green-300 hover:text-green-700 text-xs"
                  disabled={filteredBookings.length === 0}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadPDF}
                  className="h-9 hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 text-xs"
                  disabled={filteredBookings.length === 0}
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5 text-red-600" />
                  PDF
                </Button>
              </div>
              <span className="hidden sm:inline text-xs text-gray-400 dark:text-muted-foreground">
                ({filteredBookings.length} records)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookings List */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {filteredBookings.length > 0 ? (
          filteredBookings.map((booking) => {
            // Check if booking is completed (event date has passed)
            const isCompleted = (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const eventDate = new Date(booking.eventEndDate || booking.eventStartDate || booking.eventDate);
              eventDate.setHours(23, 59, 59, 999);
              const status = (booking.bookingType || booking.status || '').toLowerCase();
              return status === 'confirmed' && eventDate < today;
            })();
            
            // Calculate payment status using total paid (advance + additional payments)
            const totalAmount = Number(booking.totalAmount) || 0;
            const totalPaidAmount = calculateTotalPaid(booking);
            const isPaid = totalAmount > 0 && totalPaidAmount >= totalAmount;
            const hasDue = totalAmount > 0 && totalPaidAmount < totalAmount;
            
            return (
            <Card key={booking.id} className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden relative">
              {/* Stamps Container - Positioned at top right */}
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 pointer-events-none flex flex-col gap-1.5 items-end">
                {/* Completed Stamp */}
                {isCompleted && (
                  <div className="px-2 py-0.5 sm:px-2.5 sm:py-1 border-2 border-orange-500 rounded transform rotate-[-12deg] opacity-80">
                    <span className="text-orange-500 font-bold text-[8px] sm:text-[10px] uppercase tracking-wider">Completed</span>
                  </div>
                )}
                {/* Paid Stamp */}
                {isPaid && (
                  <div className="px-2 py-0.5 sm:px-2.5 sm:py-1 border-2 border-green-500 rounded transform rotate-[-12deg] opacity-80">
                    <span className="text-green-500 font-bold text-[8px] sm:text-[10px] uppercase tracking-wider">Paid</span>
                  </div>
                )}
                {/* Due Stamp */}
                {hasDue && (
                  <div className="px-2 py-0.5 sm:px-2.5 sm:py-1 border-2 border-red-500 rounded transform rotate-[-12deg] opacity-80">
                    <span className="text-red-500 font-bold text-[8px] sm:text-[10px] uppercase tracking-wider">Due</span>
                  </div>
                )}
              </div>
              <CardContent className="p-3 sm:p-5">
                <div className="flex flex-col gap-3">
                  {/* Header Section */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-foreground truncate">{booking.clientName}</h3>
                        <Badge variant="outline" className={`${getBookingTypeColor(booking.bookingType || booking.status)} text-[10px] sm:text-xs font-semibold`}>
                          {getBookingTypeLabel(booking.bookingType || booking.status)}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5 truncate">{booking.venue || booking.venueName}</p>
                    </div>
                  </div>

                  {/* Info Grid - Compact on mobile */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
                    <div className="flex items-center text-xs text-gray-600 dark:text-muted-foreground">
                      <Calendar className="w-3 h-3 mr-1.5 text-emerald-500 flex-shrink-0" />
                      <span className="truncate">
                        {booking.eventStartDate ? formatDate(booking.eventStartDate) : 'TBD'}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-600 dark:text-muted-foreground">
                      <Users className={`w-3 h-3 mr-1.5 flex-shrink-0 ${booking.capacityExceeded ? 'text-amber-500' : 'text-teal-500'}`} />
                      <span>{booking.guestCount || booking.guests} guests</span>
                    </div>
                    {!hideFinancials && (
                      <div className="flex items-center text-xs text-gray-600 dark:text-muted-foreground col-span-2 sm:col-span-1">
                        <DollarSign className="w-3 h-3 mr-1.5 text-cyan-500 flex-shrink-0" />
                        <span>Total: {formatCurrency(booking.totalAmount)}</span>
                      </div>
                    )}
                  </div>

                  {/* Contact Info - Stacked on mobile */}
                  <div className="flex flex-col xs:flex-row gap-1.5 xs:gap-3 text-xs text-gray-500 dark:text-muted-foreground">
                    <div className="flex items-center">
                      <Phone className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span>{formatPhoneNumber(booking.clientPhone)}</span>
                    </div>
                    {booking.clientEmail && (
                      <div className="flex items-center min-w-0">
                        <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{booking.clientEmail}</span>
                      </div>
                    )}
                  </div>

                  {/* Event Details Box */}
                  <div className="bg-gray-50 dark:bg-secondary p-2.5 rounded-lg text-xs">
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-gray-600 dark:text-muted-foreground">
                      <span><strong>Event:</strong> {booking.eventType}</span>
                      <span><strong>Time:</strong> {formatTimeToAMPM(booking.startTime)} - {formatTimeToAMPM(booking.endTime)}</span>
                    </div>
                    {booking.requirements && (
                      <p className="text-gray-500 dark:text-muted-foreground mt-1.5 line-clamp-2"><strong>Notes:</strong> {booking.requirements}</p>
                    )}
                    {booking.paymentFollowupDate && (
                      <div className="flex items-center gap-1 mt-1.5 text-orange-600">
                        <Bell className="w-3 h-3" />
                        <span>Follow-up: {formatDate(booking.paymentFollowupDate)}</span>
                      </div>
                    )}
                    
                    {/* Creator and Editor Info - Compact */}
                    {(booking.createdByName || booking.createdAt) && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-border text-[10px] text-gray-400 dark:text-muted-foreground space-y-0.5">
                        {booking.createdAt && (
                          <p>Created: {formatDateTime(booking.createdAt)}{booking.createdByName && ` by ${booking.createdByName}`}</p>
                        )}
                        {booking.updatedByName && booking.updatedAt && (
                          <p>Edited: {formatDateTime(booking.updatedAt)} by {booking.updatedByName}</p>
                        )}
                      </div>
                    )}
                  </div>

                    {/* Payment Details - Only show if financials are visible */}
                    {!hideFinancials && (
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-foreground">Payment Details</span>
                          <Badge className={`${getPaymentStatus(booking).color} text-[10px] sm:text-xs px-2`}>
                            {getPaymentStatus(booking).status}
                          </Badge>
                        </div>
                        
                        {/* Payment Summary - Stacked on mobile */}
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                            <p className="text-[10px] text-gray-500 dark:text-muted-foreground mb-0.5">Total</p>
                            <p className="font-bold text-blue-700 text-xs sm:text-sm truncate">{formatCurrency(booking.totalAmount || 0)}</p>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded-lg">
                            <p className="text-[10px] text-gray-500 dark:text-muted-foreground mb-0.5">Paid</p>
                            <p className="font-bold text-green-700 text-xs sm:text-sm truncate">{formatCurrency(calculateTotalPaid(booking))}</p>
                          </div>
                          <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg">
                            <p className="text-[10px] text-gray-500 dark:text-muted-foreground mb-0.5">Balance</p>
                            <p className="font-bold text-orange-700 text-xs sm:text-sm truncate">{formatCurrency(calculateBalance(booking))}</p>
                          </div>
                        </div>

                        {/* Show Payment History if there are payments OR advance paid */}
                        {(booking.advanceAmount > 0 || (booking.payments && booking.payments.length > 0)) && (
                          <div className="mt-3">
                            <p className="text-[10px] sm:text-xs font-semibold text-gray-700 dark:text-foreground mb-2">Payment History</p>
                            <div className="space-y-2 max-h-28 sm:max-h-40 overflow-y-auto">
                              {/* Show Advance Payment as first entry */}
                              {booking.advanceAmount > 0 && (
                                <div className="text-xs bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-2 rounded border border-green-200 dark:border-green-800">
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-green-700 dark:text-green-400">{formatCurrency(booking.advanceAmount)}</span>
                                      <Badge className="text-[9px] px-1.5 py-0 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 border-green-300">
                                        ADVANCE
                                      </Badge>
                                      {booking.advancePaymentMode && (
                                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                                          {booking.advancePaymentMode.toUpperCase()}
                                        </Badge>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-gray-400 dark:text-muted-foreground">
                                      {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : 'At Booking'}
                                    </span>
                                  </div>
                                  {booking.advancePaymentNote && (
                                    <p className="text-[10px] text-gray-500 dark:text-muted-foreground italic mt-1 truncate" title={booking.advancePaymentNote}>&quot;{booking.advancePaymentNote}&quot;</p>
                                  )}
                                  {canCreateInvoices() && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/invoices', { state: {
                                        createFromBooking: true,
                                        bookingId: booking.id,
                                        customerName: booking.clientName,
                                        customerPhone: booking.clientPhone,
                                        customerEmail: booking.clientEmail || '',
                                        venueName: booking.venueName || booking.venue,
                                        venueId: booking.venueId,
                                        paymentAmount: booking.advanceAmount,
                                        paymentMethod: booking.advancePaymentMode || 'cash',
                                        paymentDate: booking.createdAt ? new Date(booking.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                        paymentNote: booking.advancePaymentNote || '',
                                        serviceName: `Advance - ${booking.eventType || 'Event'} (${booking.venueName || booking.venue || ''})`,
                                        eventDate: booking.eventStartDate || booking.eventDate
                                      }});
                                    }}
                                    className="mt-1.5 flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 px-1.5 py-0.5 rounded transition-colors"
                                    title="Create Invoice for this payment"
                                    data-testid={`create-invoice-advance-${booking.id}`}
                                  >
                                    <Receipt className="w-3 h-3" />
                                    Create Invoice
                                  </button>
                                  )}
                                </div>
                              )}
                              {/* Show subsequent payments */}
                              {booking.payments && booking.payments.map((payment, idx) => (
                                <div key={payment.id} className="text-xs bg-white dark:bg-card p-2 rounded border border-gray-200 dark:border-border hover:border-blue-300 transition-colors">
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-900 dark:text-foreground">{formatCurrency(payment.amount)}</span>
                                      <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                                        {payment.method?.toUpperCase() || 'CASH'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-[10px] text-gray-400 dark:text-muted-foreground">{new Date(payment.date).toLocaleDateString()}</span>
                                      {canEditBookings() && (
                                        <>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleEditPayment(booking, payment); }}
                                            className="p-1 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 rounded"
                                            title="Edit payment"
                                            data-testid={`edit-payment-${payment.id}`}
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleDeletePayment(booking, payment.id); }}
                                            className="p-1 text-red-600 hover:bg-red-100 dark:bg-red-900/30 rounded"
                                            title="Delete payment"
                                            data-testid={`delete-payment-${payment.id}`}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {payment.notes && (
                                    <p className="text-[10px] text-gray-500 dark:text-muted-foreground italic mt-1 truncate" title={payment.notes}>&quot;{payment.notes}&quot;</p>
                                  )}
                                  {canCreateInvoices() && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/invoices', { state: {
                                        createFromBooking: true,
                                        bookingId: booking.id,
                                        customerName: booking.clientName,
                                        customerPhone: booking.clientPhone,
                                        customerEmail: booking.clientEmail || '',
                                        venueName: booking.venueName || booking.venue,
                                        venueId: booking.venueId,
                                        paymentAmount: payment.amount,
                                        paymentMethod: payment.method || 'cash',
                                        paymentDate: payment.date ? new Date(payment.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                        paymentNote: payment.notes || '',
                                        serviceName: `Payment #${idx + 1} - ${booking.eventType || 'Event'} (${booking.venueName || booking.venue || ''})`,
                                        eventDate: booking.eventStartDate || booking.eventDate
                                      }});
                                    }}
                                    className="mt-1.5 flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 px-1.5 py-0.5 rounded transition-colors"
                                    title="Create Invoice for this payment"
                                    data-testid={`create-invoice-payment-${payment.id}`}
                                  >
                                    <Receipt className="w-3 h-3" />
                                    Create Invoice
                                  </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Vendor Details Section - Only for Confirmed Bookings */}
                    {(booking.status === 'confirmed' || booking.bookingType === 'confirmed') && (booking.cateringName || booking.decorationName) && (
                      <div className="border-t pt-3">
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-foreground flex items-center gap-1 mb-2">
                          <User className="w-3 h-3" />
                          Vendor Details
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Catering Details */}
                          {booking.cateringName && (
                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                              <div className="flex items-center gap-1 mb-1">
                                <UtensilsCrossed className="w-3 h-3 text-orange-600" />
                                <span className="text-[10px] font-semibold text-orange-700">Catering</span>
                              </div>
                              <p className="text-xs font-medium text-gray-800 dark:text-foreground truncate">{booking.cateringName}</p>
                              {booking.cateringContact && (
                                <p className="text-[10px] text-gray-600 dark:text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-2.5 h-2.5" />
                                  {booking.cateringContact}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Decoration Details */}
                          {booking.decorationName && (
                            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-2">
                              <div className="flex items-center gap-1 mb-1">
                                <Palette className="w-3 h-3 text-pink-600" />
                                <span className="text-[10px] font-semibold text-pink-700">Decoration</span>
                              </div>
                              <p className="text-xs font-medium text-gray-800 dark:text-foreground truncate">{booking.decorationName}</p>
                              {booking.decorationContact && (
                                <p className="text-[10px] text-gray-600 dark:text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-2.5 h-2.5" />
                                  {booking.decorationContact}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Action Buttons - Show text on all screens */}
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 border-t pt-3">
                    {/* Vendors Button - Only for Confirmed Bookings */}
                    {(booking.status === 'confirmed' || booking.bookingType === 'confirmed') && (
                      <Button
                        onClick={() => handleVendorClick(booking)}
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-none hover:bg-purple-50 dark:bg-purple-900/20 hover:border-purple-300 hover:text-purple-700 h-9 px-2 sm:px-3 text-[10px] sm:text-xs"
                        title="Vendors"
                      >
                        <UtensilsCrossed className="w-3.5 h-3.5 mr-1" />
                        Vendors
                      </Button>
                    )}
                    {!hideFinancials && canEditBookings() && calculateBalance(booking) > 0 && (
                      <Button
                        onClick={() => handleAddPaymentClick(booking)}
                        size="sm"
                        className="flex-1 sm:flex-none bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 h-9 px-2 sm:px-3 text-[10px] sm:text-xs"
                        title="Add Payment"
                      >
                        <Wallet className="w-3.5 h-3.5 mr-1" />
                        Add Pay
                      </Button>
                    )}
                    {canEditBookings() && (
                      <Button
                        onClick={() => handleEdit(booking)}
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-none hover:bg-emerald-50 dark:bg-emerald-900/20 hover:border-emerald-300 hover:text-emerald-700 h-9 px-2 sm:px-3 text-[10px] sm:text-xs"
                        title="Edit"
                      >
                        <Edit className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                    )}
                    {canDeleteBookings() && (
                      <Button
                        onClick={() => handleDeleteClick(booking)}
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-none hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 h-9 px-2 sm:px-3 text-[10px] sm:text-xs"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })
        ) : (
          <Card className="border-0 shadow-md">
            <CardContent className="p-8 sm:p-12 text-center">
              <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-base sm:text-lg font-medium">No bookings found</p>
              <p className="text-gray-400 dark:text-muted-foreground text-xs sm:text-sm mt-1">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
        setPaymentDialogOpen(open);
        if (!open) {
          setEditingPayment(null);
          setEditPaymentBookingId(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              {editingPayment ? 'Edit Payment' : 'Add Payment'}
            </DialogTitle>
            <DialogDescription>
              {editingPayment ? 'Update the payment details' : 'Record a new payment for this booking'}
            </DialogDescription>
          </DialogHeader>

          {bookingForPayment && (
            <div className="space-y-4">
              {/* Booking Summary */}
              <div className="p-3 bg-gray-50 dark:bg-secondary rounded-lg border space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Client:</span>
                  <span className="text-sm font-medium">{bookingForPayment.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Total Amount:</span>
                  <span className="text-sm font-bold text-blue-700">{formatCurrency(bookingForPayment.totalAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Already Paid:</span>
                  <span className="text-sm font-bold text-green-700">{formatCurrency(calculateTotalPaid(bookingForPayment))}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-foreground">Remaining Balance:</span>
                  <span className="text-sm font-bold text-orange-700">{formatCurrency(calculateBalance(bookingForPayment))}</span>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentAmount">Payment Amount *</Label>
                  <Input
                    id="paymentAmount"
                    type="number"
                    placeholder="Enter amount"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    min="0"
                    max={calculateBalance(bookingForPayment)}
                  />
                  <p className="text-xs text-gray-500 dark:text-muted-foreground">
                    Maximum: {formatCurrency(calculateBalance(bookingForPayment))}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentDate" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    Payment Received Date *
                  </Label>
                  <Input
                    id="paymentDate"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Payment Method *</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">💵 Cash</SelectItem>
                      <SelectItem value="card">💳 Card</SelectItem>
                      <SelectItem value="upi">📱 UPI</SelectItem>
                      <SelectItem value="bank_transfer">🏦 Bank Transfer</SelectItem>
                      <SelectItem value="cheque">📝 Cheque</SelectItem>
                      <SelectItem value="other">🔹 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentAccountHead" className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-blue-500" />
                    Received In Account
                  </Label>
                  {accountHeads.length === 0 ? (
                    <div className="border border-orange-200 dark:border-orange-900 rounded-md p-3 bg-orange-50 dark:bg-orange-950/30">
                      <p className="text-xs text-orange-800 dark:text-orange-200 mb-2">
                        No accounts available. They may still be loading, or permission is missing.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => loadAccountHeads()}
                          data-testid="reload-accounts-btn"
                          className="h-7 text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Reload accounts
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setPaymentDialogOpen(false); navigate('/accounts'); }}
                          data-testid="goto-accounts-btn"
                          className="h-7 text-xs"
                        >
                          Go to Accounts page
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Select value={paymentAccountHeadId} onValueChange={setPaymentAccountHeadId}>
                      <SelectTrigger data-testid="payment-account-head-trigger">
                        <SelectValue placeholder="Select account (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {accountHeads.map(account => (
                          <SelectItem key={account.id} value={account.id} data-testid={`account-head-opt-${account.id}`}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-gray-500 dark:text-muted-foreground">
                    Defaults to &quot;Others&quot; if not selected
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentNotes">Notes (Optional)</Label>
                  <Input
                    id="paymentNotes"
                    placeholder="Transaction ID, reference, etc."
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setPaymentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={editingPayment ? handleUpdatePayment : handleAddPayment}
              className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
            >
              <Receipt className="w-4 h-4 mr-2" />
              {editingPayment ? 'Update Payment' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Details Dialog */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Vendor Details
            </DialogTitle>
            <DialogDescription>
              Add catering and decoration vendor details for this booking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Catering Section */}
            <div className="space-y-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100">
              <div className="flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-orange-600" />
                <h4 className="font-semibold text-orange-700 text-sm">Catering Person</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="cateringName" className="text-xs">Name</Label>
                  <Input
                    id="cateringName"
                    placeholder="Caterer name"
                    value={vendorData.cateringName}
                    onChange={(e) => setVendorData({...vendorData, cateringName: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="cateringContact" className="text-xs">Contact No.</Label>
                  <Input
                    id="cateringContact"
                    placeholder="Phone number"
                    value={vendorData.cateringContact}
                    onChange={(e) => setVendorData({...vendorData, cateringContact: e.target.value})}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cateringNote" className="text-xs">Note</Label>
                <Input
                  id="cateringNote"
                  placeholder="Menu details, special instructions..."
                  value={vendorData.cateringNote}
                  onChange={(e) => setVendorData({...vendorData, cateringNote: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>

            {/* Decoration Section */}
            <div className="space-y-3 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-100">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-pink-600" />
                <h4 className="font-semibold text-pink-700 text-sm">Decoration</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="decorationName" className="text-xs">Name</Label>
                  <Input
                    id="decorationName"
                    placeholder="Decorator name"
                    value={vendorData.decorationName}
                    onChange={(e) => setVendorData({...vendorData, decorationName: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="decorationContact" className="text-xs">Contact No.</Label>
                  <Input
                    id="decorationContact"
                    placeholder="Phone number"
                    value={vendorData.decorationContact}
                    onChange={(e) => setVendorData({...vendorData, decorationContact: e.target.value})}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="decorationNote" className="text-xs">Note</Label>
                <Input
                  id="decorationNote"
                  placeholder="Theme, color scheme, special requirements..."
                  value={vendorData.decorationNote}
                  onChange={(e) => setVendorData({...vendorData, decorationNote: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setVendorDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleVendorSubmit}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Vendor Details
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete Booking?"
        description="This will permanently delete this booking record. This action cannot be undone."
        itemName={bookingToDelete ? `${bookingToDelete.clientName} - ${bookingToDelete.venue} (${bookingToDelete.eventStartDate ? formatDate(bookingToDelete.eventStartDate) : formatDate(bookingToDelete.eventDate)})` : ''}
        confirmText="Delete Booking"
      />
    </div>
  );
};

export default Bookings;
